import { useState } from 'react';
import {
  Wallet,
  AlertCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  ArrowLeftRight,
  Loader2,
  Calendar,
  Clock,
  User,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  apiClient,
  type AssistantTransactionSelection,
  type AssistantTransactionSelectionCandidate,
  type AssistantDeleteFinancialEntryPreview,
  type ApiError,
} from '@/lib/api-client';
import { ActionPreviewCard } from './action-preview-card';

interface TransactionSelectionCardProps {
  selection: AssistantTransactionSelection;
}

type CardState =
  | { kind: 'idle' }
  | { kind: 'loading'; candidateId: string }
  | { kind: 'proposed'; proposalId: string; preview: AssistantDeleteFinancialEntryPreview }
  | { kind: 'error'; candidateId: string; message: string };

const TYPE_LABEL: Record<string, string> = {
  income: 'Receita',
  expense: 'Despesa',
  transfer: 'Transferência',
};

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
function formatBRL(amount: number): string {
  return BRL.format(amount);
}

/** Formata 'YYYY-MM-DD' -> 'DD/MM/YYYY' sem passar por Date (evita shift de TZ). */
function formatDateBR(iso: string): string {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/** Formata 'YYYY-MM-DDTHH:mm:ss' (ja em America/Sao_Paulo) -> 'DD/MM/YYYY HH:mm'. */
function formatDateTimeBR(iso: string): string {
  const [datePart, timePart] = iso.split('T');
  const dateBR = datePart ? formatDateBR(datePart) : iso;
  const hm = timePart ? timePart.slice(0, 5) : '';
  return hm ? `${dateBR} ${hm}` : dateBR;
}

/** Traduz o erro do endpoint de proposta em mensagem util, por candidato. */
function describeError(err: unknown): string {
  const apiErr = err as Partial<ApiError> | undefined;
  const status = apiErr?.status;
  if (status === 404) {
    return 'Lançamento não encontrado (pode já ter sido excluído). Tente outro item.';
  }
  if (status === 403) {
    return 'Você não tem permissão para excluir este lançamento.';
  }
  if (typeof apiErr?.message === 'string' && apiErr.message) {
    return apiErr.message;
  }
  return 'Não foi possível preparar a exclusão. Tente novamente.';
}

/**
 * Card de selecao de lancamento para EXCLUSAO (tool select_transaction),
 * espelhando a estetica do ClientSelectionCard mas com um fluxo DIFERENTE de
 * proposito — este e o ponto central do fix do bug "exclusao falha com nao
 * encontrado" (o modelo escolhia o UUID errado do contexto ao chamar a antiga
 * tool delete_financial_entry).
 *
 * Ao clicar num candidato, o id NUNCA volta pro chat/modelo (diferente do
 * ClientSelectionCard, que reinjeta a escolha como mensagem de usuario — o
 * modelo precisa saber qual cliente foi escolhido para prosseguir). Aqui nao
 * ha nada para o modelo prosseguir: o clique chama DIRETAMENTE
 * POST /assistant/actions/financial-delete-proposal com o id do candidato
 * clicado; a resposta ({proposalId, preview}) e usada para renderizar o card
 * de confirmacao destrutivo ja existente (ActionPreviewCard, kind
 * delete_financial_entry), que segue o fluxo normal de confirmar/cancelar
 * (POST /assistant/actions/:id/confirm). Por isso o componente NAO recebe
 * (nem precisa de) um callback do pai — e auto-suficiente, do mesmo jeito que
 * o proprio ActionPreviewCard ja chama assistantConfirmAction sozinho.
 */
export function TransactionSelectionCard({ selection }: TransactionSelectionCardProps) {
  const [state, setState] = useState<CardState>({ kind: 'idle' });

  async function handleClick(candidate: AssistantTransactionSelectionCandidate) {
    if (state.kind === 'loading' || state.kind === 'proposed') return;
    setState({ kind: 'loading', candidateId: candidate.id });
    try {
      const { proposalId, preview } = await apiClient.assistantFinancialDeleteProposal(
        candidate.id,
      );
      setState({ kind: 'proposed', proposalId, preview });
    } catch (err) {
      setState({ kind: 'error', candidateId: candidate.id, message: describeError(err) });
    }
  }

  // Proposta criada: entrega o resto do fluxo (confirmar/cancelar) pro card
  // de confirmacao destrutivo padrao — nada de logica de exclusao aqui.
  if (state.kind === 'proposed') {
    return (
      <ActionPreviewCard
        action={{
          type: 'proposed_action',
          action: 'delete_financial_entry',
          proposalId: state.proposalId,
          preview: state.preview,
        }}
      />
    );
  }

  const loadingId = state.kind === 'loading' ? state.candidateId : null;
  const errorId = state.kind === 'error' ? state.candidateId : null;

  return (
    <div className="rounded-lg border border-red-300 dark:border-red-800 bg-[var(--bg)] overflow-hidden">
      {/* Cabecalho — mesmo tom de alerta do ActionPreviewCard destrutivo */}
      <div className="flex items-center gap-1.5 border-b border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
        <Wallet className="h-3.5 w-3.5" />
        {selection.candidates.length === 1
          ? 'Lançamento encontrado · confira antes de excluir'
          : 'Mais de um lançamento encontrado · escolha um para excluir'}
      </div>

      {/* Candidatos */}
      <div className="flex flex-col divide-y divide-[var(--border)]">
        {selection.candidates.map((candidate) => {
          const isLoading = loadingId === candidate.id;
          const isLocked = loadingId !== null;
          const hasError = errorId === candidate.id;
          const isIncome = candidate.type === 'income';
          const isTransfer = candidate.type === 'transfer';

          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => handleClick(candidate)}
              disabled={isLocked}
              className={cn(
                'flex flex-col items-start gap-1 px-3 py-2.5 text-left text-xs transition-colors',
                !isLocked && 'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] cursor-pointer',
                isLocked && !isLoading && 'opacity-50 cursor-not-allowed',
              )}
            >
              <div className="flex w-full flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[var(--fg-muted)]" />
                ) : isTransfer ? (
                  <ArrowLeftRight className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
                ) : isIncome ? (
                  <ArrowUpCircle className="h-3.5 w-3.5 shrink-0 text-green-600" />
                ) : (
                  <ArrowDownCircle className="h-3.5 w-3.5 shrink-0 text-red-600" />
                )}
                <span className="font-semibold text-[var(--fg)] break-words">
                  {candidate.description}
                </span>
                <span className="text-[var(--fg-muted)]">· {formatBRL(candidate.amount)}</span>
              </div>

              <span className="text-[var(--fg-muted)]">
                {TYPE_LABEL[candidate.type] ?? candidate.type}
              </span>

              <span className="inline-flex items-center gap-1 text-[var(--fg-muted)]">
                <Calendar className="h-3 w-3 shrink-0" />
                Vencimento {formatDateBR(candidate.dueDate)}
              </span>
              <span className="inline-flex items-center gap-1 text-[var(--fg-muted)]">
                <Clock className="h-3 w-3 shrink-0" />
                Lançado em {formatDateTimeBR(candidate.createdAt)}
              </span>
              {candidate.clientName && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <User className="h-3 w-3 shrink-0" />
                  {candidate.clientName}
                </span>
              )}

              {hasError && (
                <span className="inline-flex items-start gap-1 text-red-700 dark:text-red-400">
                  <AlertCircle className="h-3 w-3 shrink-0 mt-px" />
                  {state.kind === 'error' ? state.message : ''}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
